import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import Button from '../../components/button';
import { HeaderCell } from '../../components/table/headerCell';
import { getTableStyles } from '../../components/table/styles';
import { Prev, Next } from '../../assets/images';
import { formatDateTimeDisplay, isAdmin } from '../../utils/base';
import { userAPI, organizationAPI, membershipAPI } from '../../actions/imsServer';
import { API_PAGE_SIZE } from '../../constants';

const GeneralList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [meInfo, setMeInfo] = useState(null);
    const [organizationId, setOrganizationId] = useState(0);
    const ITEMS_PER_PAGE = API_PAGE_SIZE;

    // meInfo 가져오기
    useEffect(() => {
        const meInfoString = localStorage.getItem('meInfo');
        if (meInfoString) {
            try {
                setMeInfo(JSON.parse(meInfoString));
            } catch (e) {
                console.error('Failed to parse meInfo:', e);
            }
        }
    }, []);

    // admin이면 본인의 기관 ID 가져오기
    useEffect(() => {
        const fetchAdminOrganization = async () => {
            if (isAdmin(meInfo?.grade) && meInfo?.id) {
                try {
                    // grade 50, 60 모두 membership에서 가져오기
                    const membershipsResponse = await membershipAPI.getMemberships({ userId: meInfo.id, type: 0, state: 0, expand: 'organization' });
                    const memberships = membershipsResponse?.data?.memberships || [];
                    if (memberships.length > 0) {
                        setOrganizationId(memberships[0].organizationId);
                    }
                } catch (err) {
                    console.error('Failed to fetch admin organization:', err);
                }
            }
        };

        if (meInfo) {
            fetchAdminOrganization();
        }
    }, [meInfo]);

    // 일반관리자 목록 가져오기
    // - 슈퍼어드민: grade 80
    // - 어드민: 본인 기관의 type 0인 memberships (expand user)
    const fetchAdmins = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            setError(null);

            const offset = (page - 1) * ITEMS_PER_PAGE;

            let adminsData = [];
            let total = 0;

            // admin이면 getMemberships 사용
            if (isAdmin(meInfo?.grade)) {
                const response = await membershipAPI.getMemberships({
                    organizationId: organizationId || 0,
                    type: 0,
                    state: 0,
                    expand: 'user',
                    offset: offset,
                    limit: ITEMS_PER_PAGE,
                });

                const memberships = response?.data?.memberships || [];
                // membership에서 user 정보 추출
                adminsData = memberships
                    .filter(membership => membership.user && membership.user.grade === 50) // user 정보가 있는 것만
                    .map(membership => ({
                        ...membership.user
                    }));
                total = response?.data?.total || response?.total || 0;
            } else {
                // 슈퍼어드민이면 grade 80
                const apiParams = {
                    state: 0,
                    offset: offset,
                    limit: ITEMS_PER_PAGE,
                    grade: '80',
                };

                const response = await userAPI.getUsers('all', apiParams);
                adminsData = response?.data?.users || response?.users || [];
                total = response?.data?.total || response?.total || 0;
            }

            setAdmins(adminsData);
            setTotalCount(total);
        } catch (err) {
            console.error('Failed to fetch admins:', err);
            setError(t('general_admin_list_load_failed'));
        } finally {
            setLoading(false);
        }
    }, [meInfo, organizationId]);

    // meInfo와 organizationId가 준비되면 데이터 로드
    useEffect(() => {
        if (meInfo) {
            // admin이면 organizationId가 필요하므로 기다림
            if (isAdmin(meInfo?.grade)) {
                if (organizationId !== null) {
                    fetchAdmins(1);
                }
            } else {
                // 슈퍼어드민이면 바로 로드
                fetchAdmins(1);
            }
        }
    }, [meInfo, organizationId]);

    // 페이지 변경 핸들러
    const handlePageChange = useCallback((newPage) => {
        if (newPage < 1) return;
        const maxPage = Math.ceil(totalCount / ITEMS_PER_PAGE);
        if (newPage > maxPage && maxPage > 0) return;

        setCurrentPage(newPage);
        fetchAdmins(newPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchAdmins는 의존성에서 제외
    }, [totalCount]);

    // 이전 페이지
    const handlePrevPage = useCallback(() => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    }, [currentPage, handlePageChange]);

    // 다음 페이지
    const handleNextPage = useCallback(() => {
        const maxPage = Math.ceil(totalCount / ITEMS_PER_PAGE);
        if (currentPage < maxPage) {
            handlePageChange(currentPage + 1);
        }
    }, [currentPage, totalCount, handlePageChange]);

    const handleEditAdmin = (e, admin) => {
        // 이벤트 전파 중지
        e.stopPropagation();
        // 일반관리자 수정 페이지로 이동
        navigate('/generals/edit', {
            state: {
                user: admin
            }
        });
    };

    const handleAddAdmin = () => {
        navigate('/generals/edit');
    };

    return (
        <ContentArea>
            {/* 제목 */}
            <Box sx={{ px: 2.5, py: 3, display: 'flex', justifyContent: 'center' }}>
                <Typography sx={{
                    fontSize: '24px',
                    fontWeight: 600,
                    letterSpacing: '-0.72px',
                }}>
                    {t('general_admin_list')}
                </Typography>
            </Box>

            {/* 로딩 상태 */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Typography>{t('general_admin_list_loading')}</Typography>
                </Box>
            )}

            {/* 에러 상태 */}
            {error && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            )}

            {/* 테이블 */}
            {!loading && (
                <Box
                    sx={{
                        px: 2.5,
                        mb: 3,
                    }}
                >
                    <TableContainer component={Paper}>
                        <Table aria-label="admin table">
                            <TableHead>
                                <TableRow>
                                    <HeaderCell label={t('administrator_name')} align="center" />
                                    <HeaderCell label={t('administrator_account')} align="center" />
                                    <HeaderCell label={t('last_login')} align="center" />
                                    <HeaderCell label={t('edit')} align="center" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.isArray(admins) && admins.length > 0 ? admins.map((admin) => (
                                    <TableRow
                                        key={admin.id}
                                        sx={{
                                            ...getTableStyles(theme).tableRowStyle
                                        }}
                                    >
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            {admin.name || admin.userName || ''}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            {admin.loginId || admin.email || ''}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            {admin.lastDate
                                                ? formatDateTimeDisplay(admin.lastDate, 'datetime')
                                                : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="normal"
                                                size="small"
                                                onClick={(e) => handleEditAdmin(e, admin)}
                                            >
                                                {t('edit')}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                                            {loading ? t('loading_data') : t('general_admin_no_data')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* 페이징 컨트롤 */}
                    {totalCount > 0 && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '20px 0',
                        }}>
                            <Box
                                onClick={handlePrevPage}
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: currentPage > 1 ? 'pointer' : 'default',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: currentPage > 1 ? 1 : 0.3,
                                }}
                            >
                                <Prev color={theme.palette.figma.white[80]} size={20} />
                            </Box>
                            <Typography sx={{
                                fontSize: '16px',
                                fontWeight: 500,
                                color: theme.palette.figma.white[80],
                                letterSpacing: '-0.48px',
                                whiteSpace: 'nowrap'
                            }}>
                                {currentPage} / {Math.ceil(totalCount / ITEMS_PER_PAGE)}
                            </Typography>
                            <Box
                                onClick={handleNextPage}
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: currentPage < Math.ceil(totalCount / ITEMS_PER_PAGE) ? 'pointer' : 'default',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: currentPage < Math.ceil(totalCount / ITEMS_PER_PAGE) ? 1 : 0.3,
                                }}
                            >
                                <Next color={theme.palette.figma.white[80]} size={20} />
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

            {/* 하단 일반관리자 추가 버튼 */}
            {!loading && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2.5, pb: 3, alignSelf: 'stretch' }}>
                    <Button
                        onClick={handleAddAdmin}
                    >
                        {t('general_admin_add')}
                    </Button>
                </Box>
            )}
        </ContentArea>
    );
};

export default GeneralList;
